import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Download, Upload, Database, Clock, HardDrive, FileJson, AlertCircle, CheckCircle } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const BackupPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [colRes, histRes] = await Promise.all([
        axios.get(`${API}/api/backup/collections`),
        axios.get(`${API}/api/backup/history`),
      ]);
      setCollections(colRes.data || []);
      setHistory(histRes.data || []);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/backup/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.setAttribute('download', `Vortekio_Backup_${now.toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup telecharge');
      loadData();
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error("Format JSON requis");
      return;
    }
    if (!window.confirm(`Importer le backup "${file.name}" ? Les donnees existantes ne seront pas ecrasees, seules les nouvelles seront ajoutees.`)) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/backup/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Import reussi: ${res.data.documents_imported} documents importes dans ${res.data.collections_imported} collections`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const totalDocs = collections.reduce((sum, c) => sum + c.count, 0);

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sauvegarde & Restauration</h1>
          <p className="text-sm text-slate-500 mt-1">Exportez et restaurez vos donnees en toute securite</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="backup-export-btn">
            <Download className="w-4 h-4" />
            {exporting ? 'Export...' : 'Exporter le backup'}
          </Button>
          <label>
            <Button variant="outline" className="gap-2 cursor-pointer" disabled={importing} asChild data-testid="backup-import-btn">
              <span>
                <Upload className="w-4 h-4" />
                {importing ? 'Import...' : 'Importer'}
              </span>
            </Button>
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 flex items-center gap-3" data-testid="stat-collections">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Collections</p>
            <p className="text-xl font-bold text-slate-900">{collections.length}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3" data-testid="stat-docs">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <FileJson className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Documents totaux</p>
            <p className="text-xl font-bold text-slate-900">{totalDocs.toLocaleString()}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3" data-testid="stat-backups">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <HardDrive className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Backups effectues</p>
            <p className="text-xl font-bold text-slate-900">{history.filter(h => h.type === 'export').length}</p>
          </div>
        </Card>
      </div>

      {/* Collections list */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Collections en base de donnees</h3>
        {loading ? (
          <p className="text-sm text-slate-400 py-4 text-center">{t('common.loading')}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {collections.map(c => (
              <div key={c.name} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs">
                <span className="text-slate-700 font-medium truncate">{c.name}</span>
                <span className="text-slate-400 ml-2 flex-shrink-0">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* History */}
      {history.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Historique des sauvegardes</h3>
          <div className="space-y-2">
            {history.map(h => (
              <div key={h.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {h.type === 'export' ?
                    <Download className="w-4 h-4 text-emerald-600" /> :
                    <Upload className="w-4 h-4 text-blue-600" />
                  }
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {h.type === 'export' ? 'Export' : 'Import'} — {h.filename || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(h.created_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">{h.collections_count || h.collections_imported || 0} collections</p>
                  <p className="text-xs text-slate-400">{h.total_documents || h.documents_imported || 0} docs</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Warning */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Important</p>
            <p className="text-xs text-amber-700 mt-1">
              L'import n'ecrase pas les donnees existantes — seuls les documents manquants sont ajoutes.
              Pour une restauration complete, contactez votre administrateur systeme.
            </p>
          </div>
        </div>
      </Card>
    </div>
    </AdminLayout>
  );
};

export default BackupPage;
